import { connect } from 'pwa-helpers';
import { html, nothing, PropertyValueMap, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { StoreState } from '../../../store/store.interface';
import '../../pagination/in-search/pagination';
import '../../search-v2/search-v2-pagination';
import { booleanConverter } from '../../../common/converters';
import {
  CatalogConfig,
  DEFAULT_CATALOG_CONFIG,
  DEFAULT_CATALOG_SELECTOR_ARIA_LABEL,
  DEFAULT_CATALOG_SELECTOR_NAME,
} from '../../search/search-catalog-selector';
import '../../search/search-filters';
import '../../search-v2/search-v2-filters';
import { FacetLabels } from './interface/tabbed-search.interface';
import { setFacetLabels } from '../../search-v2/store/search-v2.store';

/**
 * A component to display search filters and pagination controls for different search result tabs.
 * The component adapts its interface based on the active tab (Product, Support, or Articles).
 */
@customElement('mhecomm-tabbed-search-filters')
export class TabbedSearchFilters extends connect(store)(BaseComponent) {
  /**
   * The store identifier for this component
   */
  @property()
  store?: string;

  /**
   * Flag indicating if this is a product listing page (PLP) search
   */
  @property({ converter: booleanConverter })
  plpSearch = false;

  /**
   * Category identifier used for filtering search results
   */
  @property()
  categoryId: string | undefined;

  /**
   * Configuration for the search catalog selector
   * Defines available catalog options and their display settings
   */
  @property({ type: Object })
  catalogs: Array<CatalogConfig> = DEFAULT_CATALOG_CONFIG;

  /**
   * Input field name attribute for form submission
   */
  @property()
  name: string = DEFAULT_CATALOG_SELECTOR_NAME;

  /**
   * Accessible label for screen readers
   * Describes the purpose of the catalog selector for assistive technologies
   */
  @property()
  ariaLabel: string = DEFAULT_CATALOG_SELECTOR_ARIA_LABEL;

  @property({ converter: booleanConverter })
  showFacetHeader = true;

  @property({ converter: booleanConverter })
  collapseFilter = false;

  /**
   * Facet labels for filter display
   */
  @property({ type: Object })
  facetLabels?: FacetLabels;

  /**
   * Current active search tab (e.g., Product, Support, Articles)
   * Updated via Redux state changes
   */
  @state()
  activeTab = '';

  firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    // Dispatch facetLabels to store on initial load if provided
    if (this.facetLabels) {
      store.dispatch(setFacetLabels(this.facetLabels));
    }
  }

  updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.updated(_changedProperties);

    // If facetLabels prop changed and is defined, dispatch to store
    if (_changedProperties.has('facetLabels') && this.facetLabels) {
      store.dispatch(setFacetLabels(this.facetLabels));
    }
  }

  /**
   * Main render method that delegates to specialized rendering functions
   * @returns {TemplateResult | typeof nothing} The rendered component
   */
  renderComp(): TemplateResult | typeof nothing {
    return html`${this.renderFilterComponents()}`;
  }

  /**
   * Determines which components to render based on the active tab
   * @returns {TemplateResult | typeof nothing} The appropriate filter components for the active tab
   */
  renderFilterComponents(): TemplateResult | typeof nothing {
    switch (this.activeTab) {
      case 'Product':
        // For Product tab: Show both search filters and catalog selector
        return html`
          <div class="product-filters-wrapper">
            <div class="product-catalog-container">${this.showSearchCatalogs()}</div>
            <div class="product-filters-container mt-2">${this.showSearchFilters()}</div>
          </div>
        `;

      case 'Support':
        // For Support tab: Show only V2 search filters
        return html` <div class="support-filters-container">${this.showSearchV2Filters()}</div> `;

      case 'Articles':
        // For Articles tab: Show only search filters
        return html` <div class="articles-filters-container">${this.showSearchFilters()}</div> `;

      default:
        return nothing;
    }
  }

  /**
   * Updates component when the Redux state changes
   * Specifically tracks changes to the active tab
   *
   * @param {StoreState} state - The updated Redux store state
   */
  stateChanged(state: StoreState): void {
    const newTab = state.tabbedSearchActiveTabState.activeTabKey;
    if (this.activeTab !== newTab) {
      this.activeTab = newTab;
      this.requestUpdate();
    }
  }

  /**
   * Renders the standard search filters component
   * Used for Product and Articles tabs
   *
   * @returns {TemplateResult} Standard search filters component
   */
  private showSearchFilters(): TemplateResult {
    return html`
      <mhecomm-search-filters
        .store=${this.store}
        id="tabbed_search_filters"
        data-automation-id="tabbed_search_filters"
        role="region"
        aria-label="Search filters"
        showFacetHeader="${this.showFacetHeader}"
        collapseFilter="${this.collapseFilter}"
        .tabbedSearchEnabled=${true}
        categoryId="${this.categoryId}">
      </mhecomm-search-filters>
    `;
  }

  /**
   * Renders the search catalog selector component
   * Used for the Product tab
   *
   * @returns {TemplateResult} Search catalog selector component
   */
  private showSearchCatalogs(): TemplateResult {
    return html`
      <mhecomm-search-catalog-selector
        id="tabbed_search_catalogs"
        data-automation-id="tabbed_search_catalogs"
        .catalogs="${this.catalogs}"
        .name="${this.name}"
        .ariaLabel="${this.ariaLabel}"
        .tabbedSearchEnabled=${true}
        role="complementary"
        aria-label="Search catalog options">
      </mhecomm-search-catalog-selector>
    `;
  }

  /**
   * Renders the V2 search filters component
   * Used exclusively for the Support tab
   *
   * @returns {TemplateResult} V2 search filters component
   */
  private showSearchV2Filters(): TemplateResult {
    return html`
      <mhecomm-search-v2-filters
        id="tabbed_search_v2_filters"
        data-automation-id="tabbed_search_v2_filters"
        role="region"
        .tabbedSearchEnabled=${true}
        aria-label="Support search filters">
      </mhecomm-search-v2-filters>
    `;
  }
}

// Register the component in the global HTMLElement tag name map
declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-tabbed-search-filters': TabbedSearchFilters;
  }
}
